/* eslint-disable max-len */
import {
  defineComponent,
  ref,
  onMounted,
  onActivated,
  onDeactivated,
  computed,
  watch,
  nextTick,
  inject,
  onBeforeUnmount,
} from 'vue';
import router from '@/router';
import { Toast } from 'vant';
import { UBT } from '@hb/tianqi/lib/ubt.h5';
import {
  tryShowWidgetGuide, resetInteractionState, onDetailPopupOpen, onDetailPopupClose,
} from '~mall/common/utils/widgetGuide';
import { appQueryConfig } from '~mall/common/utils/url';
import { getStylesBinder } from '../../common/utils/styles';
import styles from './index.module.less';
import SelectTab, { TabOptionType } from './components/SelectTab';
import CommonRecommend from './components/CommonRecommend';
import GoodList from './components/GoodList';
import store from '../../model/store';
import { uiMgr } from '../../common/uiMgr';
import { appMgr } from '../../common/appMgr';
import { storageMgr } from '../../common/storageMgr';
import nativeMgr, { CityInfo } from '~mall/common/native/native';
import { ISubsidyActivityInfo } from '~mall/model/store/modules/ticket';
import { goTwoWheelCardPage } from '../../common/utils/jumpUrl';
import FirstDiscountsDialogNew from './components/FirstDiscountsDialogNew';
import Lottie from '~mall/components/lottie/index.vue';
import { getSystemCode } from '~mall/common/env/env';
import { getPlatform } from '~mall/constants/bike';
import { getEverydayCoupons } from '~mall/model/apis/bike';
import logger from '../../common/utils/logger';
import { CouponMallTabEnum } from './enum';
import RewardBar from './components/RewardBar';
import { fetchUserDiscount, updatePointSwitch, fetchGoodsPromo } from '~mall/model/apis/reward';


const cx = getStylesBinder(styles);

export default defineComponent({
  name: 'Mall',
  props: {
    titleBarHeight: {
      type: Number,
      default: 0,
    },
  },
  setup: (props, { expose }) => {
    const { titleBarHeight } = props;
    const styleVersion = inject('styleVersion');
    /** 类目list */
    const tabList = ref<TabOptionType[]>([]);
    /** 当前选中类目id */
    const curCategoryId = ref<string>(CouponMallTabEnum.ALL);
    const curCategoryName = ref<string>('全部');
    /** CommonRecommend 数据加载完成标志 */
    const recommendLoaded = ref(false);
    /** 定位信息 */
    const location = ref<CityInfo | null>(null);
    /** 是否展示一亿壕补弹窗 */
    const showFirstDiscountsDialog = ref(false);
    /** 新版一亿壕补弹窗是否显示用户引导 */
    const showNewDiscountsDialogUserGuide = ref(false);
    /** 侧边一亿壕补领取副标 */
    const showFixedIcon = ref(true);
    /** 一亿壕补数据 */
    const discountsRes = ref<any>({});
    // 新版一亿壕补半屏动画用户引导
    const userGuideType = ref<1 | 2 | undefined>(undefined); // 1: 全屏动画引导，2：半屏动画引导
    /** 标记是否是点击一亿壕补首惠弹窗后触发的刷新，避免重复显示引导 */
    const isClickRefresh = ref(false);
    /** 是否正在拖动 */
    const isDragging = ref(false);
    /** 是否有补贴 */
    const hasSubsidy = ref(false);
    /** 补贴活动信息 */
    const subsidyActivityInfos = ref<ISubsidyActivityInfo[]>([]);

    /** 是否是首次进入页面（用于控制needGreatValue参数） */
    const isFirstPageLoad = ref(true);

    // 小组件引导计时器（商品列表加载完成后 10 秒触发）
    const widgetGuideTimer = ref<ReturnType<typeof setTimeout> | null>(null);

    /** 初始化奖励金数据 */
    const initRewardData = async () => {
      try {
        const data = await fetchUserDiscount();
        if (data) {
          store.commit('reward/setDiscountData', data);
        }
      } catch {
        // 接口失败隐藏营销条
        store.commit('reward/setDiscountData', { points: 0, amount: 0, pointSwitch: false });
      }
      store.commit('reward/updateBarVisibility');
    };

    /** 奖励金营销条是否展示 */
    const isBarVisible = computed(() => store.state.reward.isBarVisible);
    /** 奖励金开关状态 */
    const rewardSwitchOn = computed(() => store.state.reward.userSwitch);
    /** 奖励金数据 */
    const rewardPoints = computed(() => store.state.reward.points);
    const rewardAmount = computed(() => store.state.reward.amount);

    const loadData = async () => {
      try {
        // 优先使用 路由跳转时临时指定的类目ID
        const initCategoryId = uiMgr.tempParams?.type
          || window.sessionStorage.getItem('curCategoryId')
          || appMgr.pageCategory;
        curCategoryId.value = initCategoryId || CouponMallTabEnum.ALL;

        if (await store.dispatch('ticket/actionRequestCategoryList')) {
          curCategoryName.value = tabList.value.find(
            (item: TabOptionType) => item.categoryId === initCategoryId,
          )?.categoryName || '全部';
        }
      } catch (error) {
        // noop
      }
    };

    /** 公共的sku请求参数 */
    const commonSkuReqParams = computed(() => {
      const params = {
        categoryId: curCategoryId.value || CouponMallTabEnum.ALL,
        pageNum: 1,
        isNew: true,
      };

      if (appMgr.pageCategory === params.categoryId) {
        const { spuId } = router.app.$route.query;
        Object.assign(params, { spuId });
      }

      return params;
    });
    const fetchSkuList = async (params?: {
      categoryId: string;
      pageNum: number;
      isNew: boolean;
      isFirstReq?: boolean;
      needGreatValue?: boolean;
    }) => {
      store.dispatch('ticket/actionRequestSkuList', {
        ...params,
        needTwoWheel: true,
      });
    };

    /** 奖励金开关切换 */
    const handleRewardSwitchChange = async (val: boolean) => {
      try {
        // 先同步开关状态到服务端
        await updatePointSwitch(val);
        store.commit('reward/setUserSwitch', val);
      } catch {
        // 使用接口返回的错误提示
        // Toast('操作失败，请重试');
      }
      // 列表刷新由 watch 统一触发，此处不再手动调用 fetchSkuList
      // 刷新超值精选商品价格（超值精选不随列表接口刷新，需单独调用 promo 接口）
      const greatValueSpuIds = (store.state.ticket.greatValueModule || [])
        .map((item) => String(item.spuId))
        .filter((id: string) => id && id !== 'undefined');
      if (greatValueSpuIds.length > 0) {
        fetchGoodsPromo({
          spuIds: greatValueSpuIds,
          categoryId: curCategoryId.value || CouponMallTabEnum.ALL,
          cityCode: location.value?.cityCode || '',
          _uuid: window.$$user?.userGuid,
          systemCode: getSystemCode(),
        }).then((promoData) => {
          if (promoData) {
            store.commit('ticket/updateSkuPromoInfo', promoData);
          }
        }).catch(() => { /* 刷新失败不阻塞 */ });
      }
    };

    // 奖励金数据是否已完成首次初始化（防止首次 initRewardData 触发 watch 导致重复请求）
    const rewardInitialized = ref(false);

    // 监听奖励金金额和开关状态，变化时重新请求商品列表价格
    watch(
      () => [store.state.reward.amount, store.state.reward.userSwitch],
      () => {
        if (!rewardInitialized.value) return;
        // 下单后刷新流程中跳过：refreshRewardData 更新 amount 会触发此 watch，
        // 但此时不能重载列表，否则会销毁浮层导致详情刷新失败
        if (store.state.reward.isPostOrderRefreshing) return;
        fetchSkuList(commonSkuReqParams.value);
      },
    );

    const actionCity = (cityName: string) => store.dispatch('city/actionCity', cityName);
    const defaultCity = computed(() => store.state.city.defaultCity);
    const getCityInfo = async () => {
      if (defaultCity.value === '' || !location.value?.cityCode) {
        try {
          const res = await nativeMgr.getCityInfo();
          logger('列表获取定位信息', res);
          if (res && 'city' in res) {
            actionCity(res.city);
          } else {
            actionCity('');
          }
          location.value = res;
        } catch (e) {
          actionCity('定位失败');
          location.value = null;
        } finally {
          UBT.pageView({
            pageId: 'platform_couponmall',
            categoryId: 'platform',
            businessInfo: {
              experimentalGroup: styleVersion,
              cityName: location.value?.city || defaultCity.value,
              adsource: appQueryConfig.adsource,
            },
          });
        }
      }
    };

    onMounted(() => {
      loadData();
      getCityInfo();
    });

    // 小组件引导：页面激活时启动 10 秒计时器（tryShowWidgetGuide 内部有疲劳度/每日限制判断，无需额外标记）
    const startWidgetGuideTimer = () => {
      // 已有计时器运行中则不重复启动
      if (widgetGuideTimer.value) return;
      const listLength = store.state.ticket.skuDatas?.[curCategoryId.value]?.list?.length;
      if (listLength && listLength > 0) {
        logger('[小组件] 启动 5 秒计时器');
        widgetGuideTimer.value = setTimeout(async () => {
          widgetGuideTimer.value = null;
          logger('[小组件] 列表页 5 秒计时结束，触发引导');
          await tryShowWidgetGuide({ pageType: 'mallList', source: 'mall_list', categoryId: curCategoryId.value });
        }, 5000);
      }
    };

    // 监听商品列表加载完成，启动计时器
    watch(
      () => store.state.ticket.skuDatas?.[curCategoryId.value]?.list?.length,
      (listLength) => {
        if (listLength && listLength > 0) {
          startWidgetGuideTimer();
        }
      },
    );

    // keep-alive 激活时刷新奖励金数据（首次进入 + 从详情页返回列表页时均触发）
    onActivated(async () => {
      await initRewardData();
      // 首次初始化完成后开启 watch，后续 amount/userSwitch 变化才触发列表刷新
      if (!rewardInitialized.value) {
        rewardInitialized.value = true;
      }
      // 小组件引导：从其他页面回来后重新启动 10 秒计时器
      startWidgetGuideTimer();
    });

    // keep-alive 失活时清理小组件引导计时器（离开页面停止弹窗）
    onDeactivated(() => {
      if (widgetGuideTimer.value) {
        clearTimeout(widgetGuideTimer.value);
        widgetGuideTimer.value = null;
        logger('[小组件] 列表页失活，清理计时器');
      }
      // 重置交互冲突状态，避免跨页面污染
      resetInteractionState();
    });

    onBeforeUnmount(() => {
      // 清理小组件引导计时器
      if (widgetGuideTimer.value) {
        clearTimeout(widgetGuideTimer.value);
        widgetGuideTimer.value = null;
      }

      UBT.pageViewOut({
        pageId: 'platform_couponmall',
        categoryId: 'platform',
        businessInfo: {
          experimentalGroup: styleVersion,
          cityName: location.value?.city || defaultCity.value,
          adsource: appQueryConfig.adsource,
        },
      });
    });

    /** 点击tab */
    const handleTabChange = (row: TabOptionType) => {
      curCategoryId.value = row.categoryId;
      curCategoryName.value = row.categoryName;
      window.sessionStorage.setItem('curCategoryId', row.categoryId);
    };

    /** 点击一亿壕补 */
    const handleFirstDiscountsDialogClick = async () => {
      showFirstDiscountsDialog.value = false;
      // 一亿壕补首惠弹窗关闭，恢复小组件弹窗
      onDetailPopupClose();
      // 标记为点击刷新，避免 watch 中重复触发引导
      isClickRefresh.value = true;
      UBT.clickButton({
        pageId: 'platform_couponmall',
        buttonName: 'platform_discountPopup',
        categoryId: 'platform',
        businessInfo: {
          tabName: curCategoryName.value,
        },
      });
      fetchSkuList(commonSkuReqParams.value);
    };

    const updateUserGuideCount = () => {
      showNewDiscountsDialogUserGuide.value = false;
      // 一亿壕补半屏动画引导关闭，恢复小组件弹窗
      onDetailPopupClose();
      const newEverydayUserGuideCount = storageMgr.getItem('NEW_EVERYDAY_USERGUIDE_COUNT') || 0;
      storageMgr.setItem(
        'NEW_EVERYDAY_USERGUIDE_COUNT',
        newEverydayUserGuideCount + 1,
      );
    };

    const handleCouponClick = async () => {
      try {
        const city = {
          cityCode: location.value?.cityCode,
          adCode: location.value?.adCode,
          lat: location.value?.latitude,
          lng: location.value?.longitude,
        };
        const { version } = await nativeMgr.getDeviceInfo();
        const systemCode = getSystemCode();
        if (subsidyActivityInfos.value.length === 0) {
          logger('没有补贴活动信息，不调用接口');
        }
        const results = await Promise.all(
          subsidyActivityInfos.value.map(({ channelCode, businessId }) => getEverydayCoupons({
            ...city,
            version,
            platform: getPlatform(),
            channelCodes: [channelCode],
            businessId,
            systemCode,
          })),
        );
        const res = results[results.length - 1];
        const { discountsList = [] } = res;
        if (discountsList.length === 0) {
          Toast('活动已结束');
          return;
        }
        UBT.clickButton({
          pageId: 'platform_couponmall',
          buttonName: 'platform_randomDiscount',
          categoryId: 'platform',
          businessInfo: {
            tabName: curCategoryName.value,
          },
        });

        discountsRes.value = res;
        if (discountsList.length) {
          UBT.moduleExpose({
            pageId: 'platform_couponmall',
            categoryId: 'platform',
            moduleId: 'platform_discountPopup',
            contentId: 'platform_discountPopup',
            exposureTimes: 1,
            businessInfo: {
              tabName: curCategoryName.value,
            },
          });

          showFirstDiscountsDialog.value = true;
          // 一亿壕补首惠弹窗打开，暂停小组件弹窗
          onDetailPopupOpen();
        }
      } catch (e) {
        logger(e);
      }
    };

    // 新版一亿壕补图标拖动，兼并点击事件
    const handleDragStart = (event: TouchEvent | MouseEvent) => {
      event.preventDefault();
      isDragging.value = false;
      const startY = event.type === 'touchstart'
        ? (event as TouchEvent).touches[0].clientY
        : (event as MouseEvent).clientY;
      const elem = event.currentTarget as HTMLElement;
      const originalTop = elem.getBoundingClientRect().top;

      const handleDragging = (moveEvent: TouchEvent | MouseEvent) => {
        isDragging.value = true;
        const moveY = moveEvent.type === 'touchmove'
          ? (moveEvent as TouchEvent).touches[0].clientY
          : (moveEvent as MouseEvent).clientY;
        let newY = originalTop + (moveY - startY);
        if (newY < 0) {
          newY = 0;
        }
        if (newY > window.innerHeight - elem.offsetHeight) {
          newY = window.innerHeight - elem.offsetHeight;
        }
        elem.style.top = `${newY}px`;
      };
      const handleDragEnd = () => {
        if (!isDragging.value) {
          handleCouponClick();
        }
        document.removeEventListener('touchmove', handleDragging);
        document.removeEventListener('touchend', handleDragEnd);
      };
      document.addEventListener('touchmove', handleDragging);
      document.addEventListener('touchend', handleDragEnd);
    };

    // 点击半屏动画领取
    const handleHalfGuideClick = () => {
      updateUserGuideCount();
      handleCouponClick();
      showFixedIcon.value = false;
    };

    const handleVerydayCouponNewIconExpose = () => {
      UBT.moduleExpose({
        pageId: 'platform_couponmall',
        categoryId: 'platform',
        moduleId: 'platform_randomDiscount',
        contentId: 'platform_randomDiscount',
        exposureTimes: 1,
        businessInfo: {
          tabName: curCategoryName.value,
        },
      });
    };

    const handleCloseUserGuide = () => {
      updateUserGuideCount();
      handleVerydayCouponNewIconExpose();
    };

    // 新版1亿壕补动画及埋点
    const showNewEveryDayPopUpUserGuide = () => {
      logger('新版一亿壕补弹窗showNewEveryDayPopUpUserGuide');
      const newEverydayUserGuideCount = storageMgr.getItem('NEW_EVERYDAY_USERGUIDE_COUNT') || 0;
      if (newEverydayUserGuideCount < 2) {
        showNewDiscountsDialogUserGuide.value = true;
        // 一亿壕补动画引导打开，暂停小组件弹窗
        onDetailPopupOpen();
        nextTick(() => {
          // 第一次展示全屏动画引导
          if (newEverydayUserGuideCount === 0) {
            userGuideType.value = 1;
            setTimeout(() => {
              storageMgr.setItem(
                'NEW_EVERYDAY_USERGUIDE_COUNT',
                newEverydayUserGuideCount + 1,
              );
              showNewDiscountsDialogUserGuide.value = false;
              // 一亿壕补全屏动画引导关闭，恢复小组件弹窗
              onDetailPopupClose();
              // 新版一亿壕补曝光埋点
              handleVerydayCouponNewIconExpose();
            }, 3000);
          } else if (newEverydayUserGuideCount === 1) {
            // 第二次展示半屏动画引导
            userGuideType.value = 2;
          }
        });
      } else {
        // 新版一亿壕补曝光埋点
        handleVerydayCouponNewIconExpose();
      }
    };

    watch(
      curCategoryId,
      (val: string, oldVal?: string) => {
        if (!val) {
          return;
        }
        window.sessionStorage.setItem('tabName', curCategoryName.value);
        if (oldVal) {
          window.sessionStorage.setItem('curCategoryId', val);
        }
        // 首次进入页面时needGreatValue为true，请求过数据以后立刻切换为false
        const needGreatValue = isFirstPageLoad.value;
        fetchSkuList({
          ...commonSkuReqParams.value,
          needGreatValue,
        });
        // 首次请求后设置为false
        if (isFirstPageLoad.value) {
          isFirstPageLoad.value = false;
        }
      },
      { immediate: true },
    );


    watch(store.state.ticket.skuDatas, (newVal) => {
      const curCategorySkuData = newVal?.[curCategoryId.value];
      hasSubsidy.value = curCategorySkuData?.subsidy?.hasSubsidy || false;
      subsidyActivityInfos.value = curCategorySkuData?.subsidy?.subsidyActivityInfos || [];
      logger('一亿壕补hasSubsidy：', hasSubsidy.value);
      if (curCategorySkuData && hasSubsidy.value) {
        // 如果是点击首惠弹窗后触发的刷新，不重复显示引导
        if (isClickRefresh.value) {
          isClickRefresh.value = false; // 重置标记
          return;
        }
        nextTick(() => {
          showNewEveryDayPopUpUserGuide();
        });
      }
    });

    watch(
      () => store.state.ticket.categoryList,
      (newVal) => {
        if (newVal && newVal.length > 0) {
          tabList.value.splice(0, tabList.value.length);
          tabList.value.push(...newVal);
        }
      },
      { immediate: true, deep: true },
    );

    // --- 商品列表相关 ---
    /** 处理两轮骑行卡支付成功 */
    const handleRidePaySuccess = () => {
      goTwoWheelCardPage();
    };

    const selectTabRef = ref<any>(null);
    // 监听tab切换
    const onTabChange = (options: { tabKey: string; }) => {
      if (
        selectTabRef.value
        && typeof selectTabRef.value.onTabChange === 'function'
      ) {
        selectTabRef.value.onTabChange(options);
      }
    };

    // 暴露方法给父组件
    expose({
      onTabChange,
    });

    return () => (
      <div class={cx('container')}>
        {/* 超值精选 */}
        <CommonRecommend
          ridePaySuccess={handleRidePaySuccess}
          class="common-recommend"
          onLoaded={() => {
            recommendLoaded.value = true;
          }}
        />
        {/* 内容包括业务线和券列表 */}
        <div
          class={cx('content-container-scroll')}
          style={{ minHeight: `calc(100vh - ${titleBarHeight}px + 2px)` }}
        >
          {/* 业务线tab */}
          <SelectTab
            ref={selectTabRef}
            tabList={tabList.value}
            titleBarHeight={titleBarHeight}
            handleTabChange={handleTabChange}
            defaultSelect={curCategoryId.value}
            recommendLoaded={recommendLoaded.value}
          />


          {/* 券列表 */}
          <div class={cx('card-list')}>
            {/* 奖励金营销条 */}
            {isBarVisible.value && (
              <RewardBar
                points={rewardPoints.value}
                amount={rewardAmount.value}
                switchOn={rewardSwitchOn.value}
                onSwitch-change={handleRewardSwitchChange}
              />
            )}

            <GoodList
              categoryId={curCategoryId.value}
              categoryName={curCategoryName.value}
              ridePaySuccess={handleRidePaySuccess}
              rewardSwitchOn={rewardSwitchOn.value}
            />
          </div>
        </div>
        <FirstDiscountsDialogNew
          discounts-res={discountsRes.value}
          show-first-discounts-dialog={showFirstDiscountsDialog.value}
          onHandleClick={handleFirstDiscountsDialogClick}
        />
        {showNewDiscountsDialogUserGuide.value && hasSubsidy.value && (
          <div class={cx('new-everyday-lottie-container')}>
            {userGuideType.value === 1 && (
              <Lottie
                key="new-everyday-lottie"
                url="https://m.hellobike.com/resource/helloyun/11929/VTRXsXSAZ0.json"
                name="new-everyday-lottie"
                class={cx('new-everyday-lottie')}
              />
            )}
            {userGuideType.value === 2 && (
              <div class={cx('half-screen-container')}>
                <Lottie
                  key="half-screen-lottie"
                  url="https://m.hellobike.com/resource/gallery/14/29nXgBS8_u.json"
                  name="half-screen-lottie"
                  class={cx('half-screen-lottie')}
                  click-func={handleHalfGuideClick}
                />
                <div class={cx('close-btn')} onClick={handleCloseUserGuide}>
                  <img
                    class={cx('close-icon')}
                    src="https://oho-image-cdn.51downapp.cn/common/75b6aebdec8a4f4d80a9d276f03fbfd5_ride-card-4.png"
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {!showNewDiscountsDialogUserGuide.value
          && showFixedIcon.value
          && hasSubsidy.value && (
            <div class={cx('lottie-icon')} onTouchstart={handleDragStart}>
              <Lottie
                key="lottie-icon"
                url="https://m.hellobike.com/resource/helloyun/11929/6_eHba4HUY.json"
                name="lottie-icon"
              />
            </div>
        )}
      </div>
    );
  },
});
